import { useEffect, useMemo, useState } from 'react';
import { LogOut, ShieldBan, ShieldCheck, ShieldOff } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useUrlFilters } from '../../hooks/admin/useUrlFilters';
import {
  useAdminUsers,
  useForceLogoutUser,
  useSetUserStatus,
  useUpdateUserRole,
} from '../../hooks/admin/useAdminUsers';
import { enumField, pageField, stringField } from '../../lib/adminFilters';
import { getApiErrorMessage } from '../../lib/apiError';
import { absoluteTime as absolute, relativeTime as relative } from '../../lib/adminFormat';
import StatusBadge, { Pill } from '../../components/admin/StatusBadge';
import Pagination from '../../components/admin/Pagination';
import InlineNotice from '../../components/admin/InlineNotice';
import ConfirmActionDialog from '../../components/admin/ConfirmActionDialog';
import type { AccountStatus, AdminUserRow } from '../../types/admin';
import type { GlobalRole } from '../../types';

const roleOptions: GlobalRole[] = ['ADMIN', 'AGENCY', 'BRAND', 'KOL'];
const statusOptions: AccountStatus[] = ['ACTIVE', 'SUSPENDED', 'BANNED'];
const PAGE_SIZE = 20;

const usersFilterSchema = {
  search: stringField('', { maxLength: 200 }),
  role: enumField(['', 'ADMIN', 'AGENCY', 'BRAND', 'KOL'], ''),
  status: enumField(['', 'ACTIVE', 'SUSPENDED', 'BANNED'], ''),
  organizationId: stringField(''),
  page: pageField(),
};

type PendingAction =
  | { kind: 'status'; user: AdminUserRow; status: AccountStatus }
  | { kind: 'logout'; user: AdminUserRow };

const ACTION_COPY: Record<AccountStatus, { title: string; confirm: string; tone: 'warn' | 'danger' | 'default'; description: string }> = {
  SUSPENDED: {
    title: 'Suspend account',
    confirm: 'Suspend',
    tone: 'warn',
    description:
      'The user is signed out everywhere immediately and cannot sign in until reactivated. They will see your reason on the blocked page.',
  },
  BANNED: {
    title: 'Ban account',
    confirm: 'Ban',
    tone: 'danger',
    description:
      'Permanent block: sessions are revoked, sign-in is refused and the Supabase auth user is banned. Use for abuse only.',
  },
  ACTIVE: {
    title: 'Reactivate account',
    confirm: 'Reactivate',
    tone: 'default',
    description: 'The user can sign in again right away.',
  },
};

export default function AdminUsers() {
  const me = useAuthStore((s) => s.user);
  const { filters, setFilters } = useUrlFilters(usersFilterSchema);
  const organizationIdFromUrl = filters.organizationId;

  // Debounced search: local input state -> URL after 300 ms.
  const [searchInput, setSearchInput] = useState(filters.search);
  useEffect(() => {
    setSearchInput(filters.search);
  }, [filters.search]);
  useEffect(() => {
    if (searchInput === filters.search) return;
    const t = setTimeout(() => setFilters({ search: searchInput }), 300);
    return () => clearTimeout(t);
  }, [searchInput, filters.search, setFilters]);

  const usersQuery = useAdminUsers({
    search: filters.search || undefined,
    role: filters.role || undefined,
    status: filters.status || undefined,
    organizationId: organizationIdFromUrl || undefined,
    page: filters.page,
    limit: PAGE_SIZE,
  });

  const users = useMemo(() => usersQuery.data?.items ?? [], [usersQuery.data]);
  const pagination = usersQuery.data?.pagination ?? null;

  const [notice, setNotice] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [savingRoleFor, setSavingRoleFor] = useState<string | null>(null);

  const roleMutation = useUpdateUserRole();
  const statusMutation = useSetUserStatus();
  const logoutMutation = useForceLogoutUser();

  const stats = useMemo(() => {
    const adminCount = users.filter((u) => u.role === 'ADMIN').length;
    const orgLinks = users.reduce((sum, u) => sum + (u.memberships?.length ?? 0), 0);
    const restricted = users.filter((u) => u.status && u.status !== 'ACTIVE').length;
    return {
      total: pagination?.total ?? 0,
      visible: users.length,
      admins: adminCount,
      memberships: orgLinks,
      restricted,
    };
  }, [users, pagination?.total]);

  const updateRole = async (userId: string, newRole: GlobalRole) => {
    setNotice(null);
    setSavingRoleFor(userId);
    try {
      await roleMutation.mutateAsync({ userId, role: newRole });
      setNotice({ tone: 'success', text: `Role updated to ${newRole}.` });
    } catch (err) {
      setNotice({ tone: 'error', text: getApiErrorMessage(err, 'Failed to update user role') });
    } finally {
      setSavingRoleFor(null);
    }
  };

  const runPending = async (reason: string) => {
    if (!pending) return;
    const name = pending.user.fullName || pending.user.email || pending.user.id;
    try {
      if (pending.kind === 'status') {
        const result = await statusMutation.mutateAsync({
          userId: pending.user.id,
          status: pending.status,
          reason: reason || undefined,
        });
        const extra =
          pending.status === 'BANNED' && result.supabaseBanApplied === false
            ? ' (Supabase ban could not be applied; the API still blocks this account.)'
            : '';
        setNotice({ tone: 'success', text: `${name} is now ${result.status}.${extra}` });
      } else {
        await logoutMutation.mutateAsync({ userId: pending.user.id });
        setNotice({ tone: 'success', text: `${name} has been signed out of all sessions.` });
      }
      setPending(null);
    } catch {
      /* error is shown inside the dialog */
    }
  };

  const pendingError =
    pending?.kind === 'status'
      ? statusMutation.isError
        ? getApiErrorMessage(statusMutation.error, 'Failed to update status')
        : null
      : logoutMutation.isError
        ? getApiErrorMessage(logoutMutation.error, 'Failed to sign the user out')
        : null;

  const openAction = (action: PendingAction) => {
    statusMutation.reset();
    logoutMutation.reset();
    setNotice(null);
    setPending(action);
  };

  const listError = usersQuery.isError ? getApiErrorMessage(usersQuery.error, 'Failed to load users') : null;
  const loading = usersQuery.isPending;

  return (
    <div className="space-y-6 text-glass-text">
      <div>
        <h1 className="page-title">User Management</h1>
        <p className="text-glass-text-2 text-sm mt-1">Platform-wide user directory, lifecycle and role controls</p>
        {organizationIdFromUrl && (
          <p className="mt-2 text-sm text-accent2">
            Filtered by organization ID: <span className="font-mono">{organizationIdFromUrl}</span>{' '}
            <button
              type="button"
              className="ml-2 text-xs underline text-glass-text-2 hover:text-glass-text"
              onClick={() => setFilters({ organizationId: '' })}
            >
              clear
            </button>
          </p>
        )}
      </div>

      {listError && <InlineNotice tone="error">{listError}</InlineNotice>}
      {notice && (
        <InlineNotice tone={notice.tone} onDismiss={() => setNotice(null)}>
          {notice.text}
        </InlineNotice>
      )}

      <div className="grid md:grid-cols-4 gap-4">
        <div className="glass-card p-5">
          <div className="text-sm text-glass-text-2">Total Users</div>
          <div className="stat-display text-2xl mt-1">{stats.total}</div>
        </div>
        <div className="glass-card p-5">
          <div className="text-sm text-glass-text-2">Visible Results</div>
          <div className="stat-display text-2xl mt-1 text-accent2">{stats.visible}</div>
        </div>
        <div className="glass-card p-5">
          <div className="text-sm text-glass-text-2">Suspended / banned (visible)</div>
          <div className={`stat-display text-2xl mt-1 ${stats.restricted > 0 ? 'text-[rgb(var(--warn))]' : ''}`}>
            {stats.restricted}
          </div>
        </div>
        <div className="glass-card p-5">
          <div className="text-sm text-glass-text-2">Org Memberships</div>
          <div className="stat-display text-2xl mt-1 text-[rgb(var(--pos))]">{stats.memberships}</div>
        </div>
      </div>

      <div className="glass-card p-4 grid md:grid-cols-3 gap-4">
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search by name or email"
          className="glass-input w-full px-4 py-2"
        />
        <select
          value={filters.role}
          onChange={(e) => setFilters({ role: e.target.value as typeof filters.role })}
          className="glass-input w-full px-4 py-2"
        >
          <option value="">All roles</option>
          {roleOptions.map((role) => (
            <option key={role} value={role}>
              {role}
            </option>
          ))}
        </select>
        <select
          value={filters.status}
          onChange={(e) => setFilters({ status: e.target.value as typeof filters.status })}
          className="glass-input w-full px-4 py-2"
        >
          <option value="">All statuses</option>
          {statusOptions.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-glass bg-glass-2/30">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-glass-text-3 uppercase">User</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-glass-text-3 uppercase">Role</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-glass-text-3 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-glass-text-3 uppercase">Organizations</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-glass-text-3 uppercase">Last sign-in</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-glass-text-3 uppercase">Last seen</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-glass-text-3 uppercase">Source</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-glass-text-3 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-glass">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-glass-text-2">
                    Loading users...
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-glass-text-2">
                    No users found.
                  </td>
                </tr>
              ) : (
                users.map((user) => {
                  const isSelf = me?.id === user.id;
                  const isAdmin = user.role === 'ADMIN';
                  const canAct = !isSelf && !isAdmin;
                  const status: AccountStatus = user.status ?? 'ACTIVE';
                  const memberships = user.memberships ?? [];
                  return (
                    <tr key={user.id} className={`hover:bg-glass-3/40 transition-colors ${usersQuery.isFetching ? 'opacity-80' : ''}`}>
                      <td className="px-4 py-4">
                        <div className="font-medium text-glass-text">
                          {user.fullName || 'Unnamed user'}
                          {isSelf && <span className="ml-2 text-xs text-glass-text-3">(you)</span>}
                        </div>
                        <div className="text-sm text-glass-text-3">{user.email || <span className="italic">no email</span>}</div>
                        <div className="text-[11px] text-glass-text-3">
                          Joined {new Date(user.createdAt).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <select
                          value={user.role}
                          disabled={savingRoleFor === user.id || isSelf}
                          onChange={(e) => updateRole(user.id, e.target.value as GlobalRole)}
                          className="glass-input px-3 py-1.5 text-sm"
                          title={isSelf ? 'You cannot change your own role' : undefined}
                        >
                          {roleOptions.map((r) => (
                            <option key={r} value={r}>
                              {r}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-4">
                        <StatusBadge
                          status={status}
                          title={
                            user.statusReason
                              ? `${user.statusReason}${user.statusChangedAt ? ` · ${absolute(user.statusChangedAt)}` : ''}`
                              : absolute(user.statusChangedAt)
                          }
                        />
                        {user.bannedUntil && (
                          <div className="mt-1 text-[11px] text-glass-text-3" title="Supabase auth ban">
                            auth ban until {absolute(user.bannedUntil)}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-wrap gap-2">
                          {memberships.length === 0 && (
                            <span className="text-xs text-glass-text-3">No membership</span>
                          )}
                          {memberships.map((m) => (
                            <span
                              key={m.id}
                              className={`status-pill inline-flex items-center gap-1 ${
                                m.organization?.status === 'SUSPENDED' ? 'status-pill-warn' : 'status-pill-info'
                              }`}
                              title={m.organization?.status === 'SUSPENDED' ? 'Organisation suspended' : undefined}
                            >
                              {m.organization?.name ?? '—'} ({m.roleInOrg})
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-glass-text-2" title={absolute(user.lastSignInAt)}>
                        {relative(user.lastSignInAt)}
                      </td>
                      <td className="px-4 py-4 text-sm text-glass-text-2" title={absolute(user.lastSeenAt)}>
                        {relative(user.lastSeenAt)}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="text-sm text-glass-text-2" title={user.signupIp ? `Signup IP ${user.signupIp}` : undefined}>
                            {user.signupSource || user.provider || '—'}
                          </span>
                          {user.provider && user.signupSource && user.provider !== user.signupSource && (
                            <span className="text-xs text-glass-text-3">· {user.provider}</span>
                          )}
                          {user.emailConfirmed === false && (
                            <Pill tone="warn" title="Email address not confirmed">
                              unconfirmed
                            </Pill>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        {canAct ? (
                          <div className="flex flex-wrap justify-end gap-1.5">
                            {status === 'ACTIVE' && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => openAction({ kind: 'status', user, status: 'SUSPENDED' })}
                                  className="btn-glass inline-flex items-center gap-1 px-2.5 py-1 text-xs text-[rgb(var(--warn))]"
                                >
                                  <ShieldOff className="h-3.5 w-3.5" aria-hidden />
                                  Suspend
                                </button>
                                <button
                                  type="button"
                                  onClick={() => openAction({ kind: 'status', user, status: 'BANNED' })}
                                  className="btn-glass inline-flex items-center gap-1 px-2.5 py-1 text-xs text-[rgb(var(--danger))]"
                                >
                                  <ShieldBan className="h-3.5 w-3.5" aria-hidden />
                                  Ban
                                </button>
                                <button
                                  type="button"
                                  onClick={() => openAction({ kind: 'logout', user })}
                                  className="btn-glass inline-flex items-center gap-1 px-2.5 py-1 text-xs"
                                  title="Invalidate all sessions"
                                >
                                  <LogOut className="h-3.5 w-3.5" aria-hidden />
                                  Sign out
                                </button>
                              </>
                            )}
                            {status === 'SUSPENDED' && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => openAction({ kind: 'status', user, status: 'ACTIVE' })}
                                  className="btn-glass inline-flex items-center gap-1 px-2.5 py-1 text-xs text-[rgb(var(--pos))]"
                                >
                                  <ShieldCheck className="h-3.5 w-3.5" aria-hidden />
                                  Reactivate
                                </button>
                                <button
                                  type="button"
                                  onClick={() => openAction({ kind: 'status', user, status: 'BANNED' })}
                                  className="btn-glass inline-flex items-center gap-1 px-2.5 py-1 text-xs text-[rgb(var(--danger))]"
                                >
                                  <ShieldBan className="h-3.5 w-3.5" aria-hidden />
                                  Ban
                                </button>
                              </>
                            )}
                            {status === 'BANNED' && (
                              <button
                                type="button"
                                onClick={() => openAction({ kind: 'status', user, status: 'ACTIVE' })}
                                className="btn-glass inline-flex items-center gap-1 px-2.5 py-1 text-xs text-[rgb(var(--pos))]"
                              >
                                <ShieldCheck className="h-3.5 w-3.5" aria-hidden />
                                Unban
                              </button>
                            )}
                          </div>
                        ) : (
                          <div className="text-right text-xs text-glass-text-3">{isSelf ? '—' : 'admin'}</div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          pagination={pagination}
          onPageChange={(page) => setFilters({ page })}
          label="users"
          isFetching={usersQuery.isFetching && !usersQuery.isPending}
        />
      </div>

      {pending && pending.kind === 'status' && (
        <ConfirmActionDialog
          isOpen
          onClose={() => setPending(null)}
          title={`${ACTION_COPY[pending.status].title}: ${pending.user.fullName || pending.user.email || 'user'}`}
          description={ACTION_COPY[pending.status].description}
          confirmLabel={ACTION_COPY[pending.status].confirm}
          tone={ACTION_COPY[pending.status].tone}
          requireReason={pending.status !== 'ACTIVE'}
          optionalReason={pending.status === 'ACTIVE'}
          isPending={statusMutation.isPending}
          error={pendingError}
          onConfirm={runPending}
        />
      )}
      {pending && pending.kind === 'logout' && (
        <ConfirmActionDialog
          isOpen
          onClose={() => setPending(null)}
          title={`Sign out ${pending.user.fullName || pending.user.email || 'user'} everywhere`}
          description="All of this user's sessions (web, extension) are invalidated. They can sign in again immediately."
          confirmLabel="Sign out"
          tone="warn"
          isPending={logoutMutation.isPending}
          error={pendingError}
          onConfirm={runPending}
        />
      )}
    </div>
  );
}
